"use client";

import { CircleAlert } from "lucide-react";
import { Icon } from "../../components/icon";
import { Label, Select } from "../../components/input";
import { Field } from "../fields";
import { localName } from "../localized";
import { type Translate, useText } from "../text";
import type { AttributeDefinition, CategoryView } from "./product-form";

type Values = Readonly<Record<string, string | readonly string[]>>;

/** The attribute's label with its unit and whether it is required, all as copy. */
function AttributeLabel({ definition, htmlFor }: { definition: AttributeDefinition; htmlFor?: string }) {
  const { t, locale } = useText();
  const unit = definition.type === "number" ? definition.unit : null;
  const content = (
    <>
      {localName(definition.label, locale)}
      {unit !== null && (
        <span className="ms-1 text-ink-muted">{t("products.attributes.unit", { unit })}</span>
      )}
      {definition.required && (
        <span className="ms-1 text-ink-muted">{t("products.attributes.required")}</span>
      )}
    </>
  );
  return htmlFor === undefined ? (
    <legend className="mb-1.5 text-small font-medium text-ink-body">{content}</legend>
  ) : (
    <Label htmlFor={htmlFor}>{content}</Label>
  );
}

/** A number's limits in words: both ends, or the one there is. */
function limits(t: Translate, min: number | null, max: number | null): string | undefined {
  if (min !== null && max !== null)
    return t("products.attributes.range", { min: String(min), max: String(max) });
  if (min !== null) return t("products.attributes.atLeast", { min: String(min) });
  if (max !== null) return t("products.attributes.atMost", { max: String(max) });
  return undefined;
}

/**
 * The leaf category's attributes as a form built from its schema (CAT-2): text, numbers with their unit and limits,
 * one or several choices, and yes or no. Values stay as typed until the product is saved.
 */
export function AttributeFields({
  schema,
  values,
  onChange,
  issue,
}: {
  schema: CategoryView["attributeSchema"] | undefined;
  values: Values;
  onChange: (key: string, value: string | readonly string[]) => void;
  issue: (path: string) => string | undefined;
}) {
  const { t, locale } = useText();
  if (schema === undefined)
    return <p className="text-body text-ink-muted">{t("products.attributes.retired")}</p>;
  if (schema === null || schema.attributes.length === 0)
    return <p className="text-body text-ink-muted">{t("products.attributes.none")}</p>;
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {schema.attributes.map((definition) => {
        const id = `attribute-${definition.key}`;
        const error = issue(`attributes.${definition.key}`);
        const raw = values[definition.key];
        const text = typeof raw === "string" ? raw : "";
        const errorText = error !== undefined && (
          <p id={`${id}-error`} className="flex items-start gap-1.5 text-caption text-tone-problem-fg">
            <Icon icon={CircleAlert} size={16} className="mt-px size-3.5 shrink-0" />
            {error}
          </p>
        );
        const name = localName(definition.label, locale);
        const label = definition.required ? `${name} ${t("products.attributes.required")}` : name;
        switch (definition.type) {
          case "text":
          case "number":
            // A number's unit sits inside the control on the right (docs/design/meridian.md, Field).
            return (
              <Field
                key={definition.key}
                id={id}
                label={label}
                value={text}
                unit={definition.type === "number" ? (definition.unit ?? undefined) : undefined}
                inputMode={definition.type === "number" ? "decimal" : undefined}
                maxLength={definition.type === "text" ? definition.maxLength : 30}
                onChange={(event) => onChange(definition.key, event.target.value)}
                error={error}
                hint={definition.type === "number" ? limits(t, definition.min, definition.max) : undefined}
              />
            );
          case "boolean":
            return (
              <div key={definition.key} className="flex flex-col gap-1.5">
                <AttributeLabel definition={definition} htmlFor={id} />
                <Select
                  id={id}
                  value={text}
                  aria-invalid={error !== undefined}
                  aria-describedby={error === undefined ? undefined : `${id}-error`}
                  onChange={(event) => onChange(definition.key, event.target.value)}
                >
                  <option value="">{t("products.attributes.unset")}</option>
                  <option value="true">{t("catalog.yes")}</option>
                  <option value="false">{t("catalog.no")}</option>
                </Select>
                {errorText}
              </div>
            );
          case "choice": {
            if (!definition.multiple)
              return (
                <div key={definition.key} className="flex flex-col gap-1.5">
                  <AttributeLabel definition={definition} htmlFor={id} />
                  <Select
                    id={id}
                    value={text}
                    aria-invalid={error !== undefined}
                    onChange={(event) => onChange(definition.key, event.target.value)}
                  >
                    <option value="">{t("products.attributes.unset")}</option>
                    {definition.options.map((option) => (
                      <option key={option.value} value={option.value}>
                        {localName(option.label, locale)}
                      </option>
                    ))}
                  </Select>
                  {errorText}
                </div>
              );
            const chosen = Array.isArray(raw) ? (raw as readonly string[]) : [];
            return (
              <fieldset key={definition.key} className="flex flex-col gap-1.5">
                <AttributeLabel definition={definition} />
                {definition.options.map((option) => (
                  <label key={option.value} className="flex items-center gap-2.5 text-body text-ink">
                    <input
                      type="checkbox"
                      className="size-4 accent-brand-600"
                      checked={chosen.includes(option.value)}
                      onChange={() =>
                        onChange(
                          definition.key,
                          chosen.includes(option.value)
                            ? chosen.filter((value) => value !== option.value)
                            : [...chosen, option.value],
                        )
                      }
                    />
                    {localName(option.label, locale)}
                  </label>
                ))}
                {errorText}
              </fieldset>
            );
          }
          default:
            return null;
        }
      })}
    </div>
  );
}
